<template>
  <div class="mt-6 leading-normal text-sm">
    <div class="flex flex-col mb-6" v-if="form">
      <div class="mb-6">
        <h2>{{ form.name }}</h2>
        <p class="text-xs uppercase" :class="form.deleted_at ? 'text-red-dark' : 'text-green-dark'">
          <i class="fas fa-circle"></i> {{ form.deleted_at ? 'Inactive' : 'Active' }}
        </p>
      </div>
      <div v-if="form.status_id !== 1">
        <button @click="back" class="bg-white hover:bg-grey text-primary py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-arrow-left"></i> Campaigns
        </button>
      </div>
      <div v-else class="flex justify-end w-full">
        <button @click="back" class="bg-white hover:bg-grey text-primary py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-arrow-left"></i> Campaigns
        </button>

        <button v-if="form.status_id === 1 && !generated && !form.deleted_at" @click="showGenerateModal = true" class="bg-white hover:bg-grey text-primary py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-sync"></i> Generate Edition
        </button>

        <button v-if="form.deleted_at" @click="showActivationModal = true" class="bg-green-dark hover:bg-green text-white py-2 px-6 text-sm rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-check-circle"></i> Activate Campaign
        </button>
        <button v-else @click="showDeactivateModal = true" class="bg-white hover:bg-grey py-2 px-6 text-sm border border-grey-dark rounded shadow whitespace-no-wrap ml-3">
          <i class="fas fa-times-circle"></i> Deactivate Campaign
        </button>

        <button @click="save" class="bg-tertiary text-white py-2 px-6 text-sm border border-tertiary hover:border-transparent rounded shadow whitespace-no-wrap ml-3" :disabled="saving">
          <loader fill-color="white" v-if="saving" />
          <span v-else><i class="fas fa-cloud-upload-alt"></i> Save Campaign Changes</span>
        </button>
      </div>
    </div>

    <div v-if="form && form.status_id === 0" class="bg-grey-light p-3">
      <i class="fas fa-sync fa-spin fa-fw"></i> We are using AI to get your new campaign started with some pre-written content. Please wait...
    </div>
    <div v-else-if="form && form.status_id === 2" class="bg-grey-light p-3">
      <i class="fas fa-exclamation-triangle fa-fw"></i> Failed to create campaign.
    </div>
    <loader v-else-if="loading" />
    <div v-else>
      <div class="ml-3">
        <button @click="tab = 'general'" class="rounded-t-lg py-3 px-6 mr-1" :class="tab === 'general' ? 'bg-white' : 'bg-grey-light hover:bg-grey'">
          General Settings
        </button>
        <button @click="tab = 'automation'" class="rounded-t-lg py-3 px-6 mr-1" :class="tab === 'automation' ? 'bg-white' : 'bg-grey-light hover:bg-grey'">
          Automation Settings
        </button>
        <button @click="tab = 'pages'" class="rounded-t-lg py-3 px-6 mr-1" :class="tab === 'pages' ? 'bg-white' : 'bg-grey-light hover:bg-grey'">
          Page Ordering
        </button>
      </div>

      <!-- GENERAL SETTINGS -->
      <panel v-if="tab === 'general'" class="pt-6">
        <ezine-campaign-settings-tab v-model="form" :errorBag="errorBag" @back="back" />
      </panel>

      <!-- AUTOMATION SETTINGS -->
      <panel v-else-if="tab === 'automation'" class="pt-6">
        <ezine-automation-settings-tab v-model="form" :errorBag="errorBag" :offices="offices" :page-groups="pageGroups" />
      </panel>

      <!-- PAGE ORDERING -->
      <panel v-else-if="tab === 'pages'" class="pt-6">
        <ezine-page-layout-tab type="campaign" v-model="form" :errorBag="errorBag" :page-groups="pageGroups" :offices="offices" />
      </panel>
    </div>

    <modal v-if="showActivationModal" @leave="showActivationModal = false" show-close-btn width="320px">
      <h2 class="font-bold text-sm mb-1">Activate Campaign</h2>

      <p>Schedule this campaign to run every {{ interval }}, starting from the date below. If you select today's date, an edition will be generated immediately and sent to your Proofing Email Address(es). All future editions are created at 7am on the scheduled date.</p>

      <date-time-picker
        v-model="activateForm.next_delivery_at"
        format="cccc, d MMMM yyyy"
        value-format="yyyy-MM-dd"
        constraints-format="yyyy-MM-dd"
        :class="'mt-2 flex items-stretch block w-full border p-2 h-10 text-sm'+(errorBag.get('next_delivery_at') ? ' border-red-dark hover:border-red-dark focus:border-red-dark' : '')"
        :min-date="minDate"
        :max-date="maxDate"
        :time-picker="false"
        today-button
        auto-close>
        <template slot="choose-date">
          [Please Select]
        </template>
        <template slot="today">
          <button class="bg-grey-light rounded py-2 px-4 text-black">
            Set to Today
          </button>
        </template>
      </date-time-picker>

      <div class="flex justify-end mt-6">
        <button class="bg-transparent text-white py-2 px-6 text-sm bg-green-dark rounded shadow" :class="{'hover:bg-green': !activating}" @click="activate" :disabled="activating">
          <loader v-if="activating" fill-color="white" />
          <span v-else><i class="fas fa-check-circle"></i> Activate Campaign</span>
        </button>
      </div>
    </modal>

    <deletion-modal
      v-if="showDeactivateModal"
      @confirmed="deactivateCampaign"
      @leave="showDeactivateModal = false"
      @dismissed="showDeactivateModal = false"
      confirm-label="Yes, deactivate it">
      <p class="text-center">Are you sure you want to deactivate this campaign? It can be activated again later.</p>
    </deletion-modal>

    <deletion-modal
      v-if="showGenerateModal"
      @confirmed="generate"
      @leave="showGenerateModal = false"
      @dismissed="showGenerateModal = false"
      confirm-label="Yes, generate">
      <p class="text-center">Are you sure you want to generate a new, out-of-schedule edition of this campaign?</p>
    </deletion-modal>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import ErrorBag from '@/ErrorBag'
import { mapActions, mapState } from 'vuex'
import { map } from 'lodash'
import moment from 'moment'
import randomstring from 'randomstring'
import EzineCampaignSettingsTab from './tabs/EzineCampaignSettingsTab.vue'
import EzineAutomationSettingsTab from './tabs/EzineAutomationSettingsTab.vue'
import EzinePageLayoutTab from './tabs/EzinePageLayoutTab.vue'
import DateTimePicker from 'vue-vanilla-datetime-picker'
import DeletionModal from '@/components/Modals/DeletionModal'

export default {
  components: { EzineCampaignSettingsTab, EzineAutomationSettingsTab, EzinePageLayoutTab, DateTimePicker, DeletionModal },
  props: {
    campaignId: {
      required: true
    }
  },
  data () {
    return {
      loading: true,
      form: null,
      tab: 'general',
      errorBag: new ErrorBag(),
      pageGroups: null,
      offices: null,
      saving: false,
      showActivationModal: false,
      activating: false,
      activateForm: {
        next_delivery_at: moment().format('YYYY-MM-DD')
      },
      showDeactivateModal: false,
      deactivating: false,
      timer: null,
      generating: false,
      generated: false,
      showGenerateModal: false
    }
  },
  computed: {
    ...mapState(['selectedOffice']),
    interval () {
      if (!this.form) {
        return null
      }
      switch (this.form.frequency_id) {
        case 0: return 'week'
        case 2: return 'month'
        case 3: return '2 months'
        default: return 'fortnight'
      }
    },
    minDate () {
      return moment().format('YYYY-MM-DD')
    },
    maxDate () {
      return moment().add(2, 'months').format('YYYY-MM-DD')
    }
  },
  created () {
    this.init()
  },
  beforeDestroy () {
    clearTimeout(this.timer)
  },
  methods: {
    ...mapActions(['displayToastNotification']),
    init () {
      this.fetchCampaign()
    },
    fetchCampaign () {
      return v2Api.get(`/ezine-campaigns/${this.campaignId}`).then((response) => {
        response.data.pages = map(response.data.pages, (page) => {
          page.ref = randomstring.generate()
          return page
        })
        this.form = response.data

        if (this.form.status_id === 0) {
          this.timer = setTimeout(this.fetchCampaign, 10000)
          return
        }

        Promise.all([
          this.getPageGroups(),
          this.getOffices()
        ]).then(() => {
          this.loading = false
        })
      })
    },
    getPageGroups () {
      return v2Api.get('ezine-page-groups', {
        params: {
          client_id: this.selectedOffice.client_id,
          ezine_campaign_id: this.campaignId,
          with_network_groups: 1
        }
      }).then((response) => {
        const orderMap = ['App\\Models\\EzineCampaign', 'App\\Models\\Client', 'App\\Models\\Network']
        this.pageGroups = response.data.sort(function (a, b) {
          return orderMap.indexOf(a.assignable_type) > orderMap.indexOf(b.assignable_type)
        })
      })
    },
    getOffices () {
      return v2Api.get('office/with-locations', {
        params: {
          client_id: this.selectedOffice.client_id
        }
      }).then((response) => {
        this.offices = response.data
      })
    },
    back () {
      this.$emit('change-campaign', null)
    },
    save () {
      this.saving = true
      this.errorBag.clear()
      let form = {
        ...this.form,
        pages: this.form.pages.map((page) => {
          let output = {
            content_type: page.content_type
          }
          if (page.content_id) {
            output.content_id = page.content_id
          } else {
            output.content = page.content
          }

          return output
        })
      }

      v2Api.patch(`ezine-campaigns/${this.form.id}`, form).then((response) => {
        for (let i = 0; i < response.data.pages.length; i++) {
          response.data.pages[i].ref = randomstring.generate()
        }
        this.form = response.data
        this.saving = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Saved',
          info: 'Changes to the campaign have been saved.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.saving = false

        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
      })
    },
    activate () {
      this.activating = true
      this.errorBag.clear()
      v2Api.patch(`ezine-campaigns/${this.form.id}/restore`, this.activateForm).then((response) => {
        this.form.deleted_at = null
        this.activating = false
        this.showActivationModal = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Activated',
          info: 'The campaign has been activated.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.activating = false
      })
    },
    deactivateCampaign () {
      this.deactivating = true
      v2Api.delete(`ezine-campaigns/${this.form.id}`).then((response) => {
        this.form.deleted_at = 1
        this.deactivating = false
        this.showDeactivateModal = false

        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Activated',
          info: 'The campaign has been deactivated.'
        })
      }, (errors) => {
        this.errorBag.store(errors.response.data)
        this.deactivating = false
      })
    },
    generate () {
      this.generating = true
      v2Api.post(`ezine-campaigns/${this.form.id}/generate`).then((response) => {
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Requested',
          info: 'A new edition has been requested and will be generated shortly.'
        })
        this.generated = true
        this.generating = false
        this.showGenerateModal = false
      }, () => {
        this.generating = false
      })
    }
  },
  watch: {
    selectedOffice () {
      this.back()
    }
  }
}
</script>
